import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule } from '@jsverse/transloco';
import { LanguageSwitcherComponent } from '../../../../../shared/i18n/language-switcher.component';

/**
 * SiteHeaderComponent — the brand-site's top navigation bar (Story 31.4). The
 * brand site had no header before this story; it is introduced here so the
 * "Documentation" entry (AC1) has a home. Slim + on-brand: the logo links to
 * the landing page, and the right side carries the language switcher + a
 * Documentation link + the Sign up CTA. The language switcher stays visible at
 * every breakpoint (it's a primary control, not a nav item); below 960 px the
 * Documentation/Sign-up links collapse into a hamburger menu. Labels resolve
 * through Transloco (DA/EN) so a live language switch re-renders them.
 *
 * The language switcher lives HERE (a normal flex child) rather than as a
 * separately `position: fixed` element layered on top of the page — that
 * fixed-positioning approach predates this header and visually collided with
 * the Sign up button once the header existed. Keep it a flex child; don't
 * reintroduce fixed positioning for it.
 *
 * The logo mirrors the home hero's dark-mode-aware <picture> pattern
 * (home.component.html) but uses the COMPACT ianua-logo-{light,dark}.svg
 * variants, not the "-brand-" ones with the wordmark baked in — that
 * component's own comment already reserved the compact mark for the header.
 * A visible "Emjo IANUA" text sits next to the mark (theme-aware color, so it
 * reads correctly in both light and dark) since the compact SVGs carry no
 * text themselves. The <a> still carries the accessible name via aria-label
 * (docs.header.homeAria) rather than relying on the visible text alone — it's
 * marginally more descriptive ("— home") and keeps the accessible name
 * independent of any future copy changes to the visible brand text. The <img>
 * stays alt="" + aria-hidden to avoid a duplicate announcement alongside it.
 */
@Component({
  selector: 'app-site-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, MatButtonModule, MatIconModule, MatMenuModule, TranslocoModule, LanguageSwitcherComponent],
  template: `
    <header class="site-header">
      <a class="brand" routerLink="/" [attr.aria-label]="'docs.header.homeAria' | transloco">
        <picture>
          <source srcset="assets/ianua-logo-dark.svg" media="(prefers-color-scheme: dark)" />
          <img src="assets/ianua-logo-light.svg" alt="" aria-hidden="true" class="brand-logo" />
        </picture>
        <span class="brand-text" aria-hidden="true">Emjo IANUA</span>
      </a>

      <div class="header-actions">
        <app-language-switcher></app-language-switcher>

        <nav class="header-nav" [attr.aria-label]="'docs.header.navAria' | transloco">
          <a class="nav-link" routerLink="/docs" routerLinkActive="active">{{ 'docs.header.documentation' | transloco }}</a>
          <a mat-flat-button color="primary" routerLink="/signup">{{ 'docs.header.signUp' | transloco }}</a>
        </nav>

        <button
          class="menu-toggle"
          mat-icon-button
          [matMenuTriggerFor]="mobileMenu"
          [attr.aria-label]="'docs.header.menu' | transloco"
        >
          <mat-icon>menu</mat-icon>
        </button>
        <mat-menu #mobileMenu="matMenu">
          <a mat-menu-item routerLink="/docs" routerLinkActive="active">{{ 'docs.header.documentation' | transloco }}</a>
          <a mat-menu-item routerLink="/signup" routerLinkActive="active">{{ 'docs.header.signUp' | transloco }}</a>
        </mat-menu>
      </div>
    </header>
  `,
  styles: [`
    .site-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 12px 24px;
      border-bottom: 1px solid var(--mat-sys-outline-variant, #e0e0e0);
      background: var(--mat-sys-surface, #fff);
    }
    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .brand-logo {
      height: 36px;
      width: auto;
      display: block;
    }
    .brand-text {
      font-size: 1.2rem;
      font-weight: 700;
      letter-spacing: -0.2px;
      color: var(--mat-sys-on-surface, #1c1b1f);
      white-space: nowrap;
    }
    .header-actions {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .header-nav {
      display: flex;
      align-items: center;
      gap: 20px;
    }
    .nav-link {
      color: var(--mat-sys-on-surface, #1c1b1f);
      text-decoration: none;
      font-weight: 500;
      padding: 4px 2px;
      border-bottom: 2px solid transparent;
    }
    .nav-link:hover { color: var(--mat-sys-primary, #1976d2); }
    .nav-link.active {
      color: var(--mat-sys-primary, #1976d2);
      border-bottom-color: var(--mat-sys-primary, #1976d2);
    }
    .menu-toggle { display: none; }

    /* Below 960px: collapse the inline nav into the hamburger menu. The
       language switcher is NOT inside .header-nav, so it stays visible. */
    @media (max-width: 959px) {
      .header-nav { display: none; }
      .menu-toggle { display: inline-flex; }
    }
  `],
})
export class SiteHeaderComponent {}
